/**
 * Token cache for Supabase authentication.
 *
 * Mirrors MSAL's approach: cache tokens in memory and only refresh when expired.
 * This prevents expensive getSession() calls on every HTTP request.
 *
 * Usage:
 *   1. Initialize on app start: initializeTokenCache(supabaseClient)
 *   2. In interceptor: const token = getTokenSync() or await getTokenAsync()
 */

import * as Sentry from "@sentry/react";
import { SupabaseClient } from "@supabase/supabase-js";

interface CachedToken {
  token: string;
  expiresAt: number;
}

let cachedToken: CachedToken | null = null;
let isInitialized = false;
let initPromise: Promise<void> | null = null;
const pageLoadTime = Date.now();

/**
 * Get elapsed time since page load in MM:SS format
 */
export function getElapsedTime(): string {
  const elapsed = Math.floor((Date.now() - pageLoadTime) / 1000);
  const minutes = Math.floor(elapsed / 60);
  const seconds = elapsed % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

/**
 * Initialize token cache with auth state change listener.
 * Should be called once in App.tsx on startup.
 */
export function initializeTokenCache(
  supabaseClient: SupabaseClient,
): (() => void) | void {
  if (isInitialized) return;

  // Subscribe to auth state changes
  const {
    data: { subscription },
  } = supabaseClient.auth.onAuthStateChange((_event, session) => {
    if (session?.access_token) {
      // Cache the token
      const expiresAt = session.expires_at
        ? session.expires_at * 1000 // Convert to milliseconds
        : Date.now() + 3600 * 1000; // Default 1 hour

      cachedToken = {
        token: session.access_token,
        expiresAt,
      };
      // Token cached silently - logged at Sentry level only
    } else {
      // Clear cache on logout
      cachedToken = null;
      // Cache cleared silently - logged at Sentry level only
    }

    isInitialized = true;
  });

  // Return unsubscribe function for cleanup if needed
  return () => subscription.unsubscribe();
}

/**
 * Get cached token synchronously (no network calls).
 *
 * Returns token if:
 * - Token is cached AND
 * - Token is not expired (with 5-minute buffer)
 *
 * This is the fast path used by the interceptor (99% of requests).
 */
export function getTokenSync(): string | null {
  if (!cachedToken) {
    return null;
  }

  // Check if token is expired (with 5-minute buffer for safety)
  const bufferMs = 5 * 60 * 1000; // 5 minutes
  const expiresInMs = cachedToken.expiresAt - bufferMs - Date.now();

  if (expiresInMs > 0) {
    return cachedToken.token; // Token valid, return from cache
  }

  // Token expired, clear cache
  const oldToken = cachedToken.token.substring(0, 20);
  console.warn(
    `[${getElapsedTime()}] [TokenCache] getTokenSync CLEARED - Expired ${Math.round(-expiresInMs / 1000)}s ago, Token: ${oldToken}...`,
  );
  cachedToken = null;
  return null;
}

/**
 * Get token asynchronously with fallback to getSession().
 *
 * Tries to return cached token first.
 * If not cached and not initialized, calls getSession() once.
 * Subsequent calls reuse the cached token.
 */
export async function getTokenAsync(
  supabaseClient: SupabaseClient,
): Promise<string | null> {
  // Fast path: token is cached and valid
  const cachedTokenValue = getTokenSync();
  if (cachedTokenValue) {
    return cachedTokenValue;
  }

  // Track if this is a retry (isInitialized was already true before this call)
  const isRetry = isInitialized;

  // If cache is empty (not initialized OR cache expired), fetch session
  if (!isInitialized || !cachedTokenValue) {
    // Share promise if multiple requests come in simultaneously
    if (!initPromise) {
      initPromise = supabaseClient.auth
        .getSession()
        .then(({ data, error }) => {
          // Only log errors on retry attempts, not initial startup
          if (isRetry) {
            if (error) {
              Sentry.captureMessage("[TOKEN] getSession() failed on retry", {
                level: "error",
                tags: {
                  event: "get_session_error",
                  error_code: error.code || "unknown",
                },
                contexts: {
                  error: {
                    message: error.message,
                    code: error.code,
                  },
                },
              });
            } else if (!data?.session?.access_token) {
              Sentry.captureMessage(
                "[TOKEN] getSession() returned no token on retry",
                {
                  level: "warning",
                  tags: {
                    event: "get_session_no_token",
                  },
                },
              );
            }
          }

          if (data?.session?.access_token) {
            const expiresAt = data.session.expires_at
              ? data.session.expires_at * 1000
              : Date.now() + 3600 * 1000;

            cachedToken = {
              token: data.session.access_token,
              expiresAt,
            };
          }
          isInitialized = true;
        })
        .catch((err) => {
          // Only log exceptions on retry attempts
          if (isRetry) {
            Sentry.captureException(err, {
              tags: {
                event: "get_session_exception",
              },
            });
          }
          isInitialized = true;
        });
    }

    await initPromise;
    // Reset promise for next potential cache miss
    initPromise = null;
    return cachedToken?.token ?? null;
  }

  return null;
}

/**
 * Check if cache is initialized.
 * Useful for debug logging and testing.
 */
export function isCacheInitialized(): boolean {
  return isInitialized;
}

/**
 * Clear the token cache (e.g., on logout).
 * Normally this happens automatically via onAuthStateChange,
 * but provided for explicit control if needed.
 */
export function clearTokenCache(): void {
  cachedToken = null;
}
